// AutoResizeTextarea
//
// A textarea that behaves like a writing surface instead of a standard
// form field: it starts small, grows with content, never shows the
// browser's manual resize handle, and never scrolls internally during
// normal writing (the page/container scrolls instead) unless `maxRows`
// is set, in which case it caps out and scrolls internally past that cap.
//
// Registered as a Plasmic code component in ./plasmic-host-registrations.js.
// This file is hand-authored and owned by you — safe to edit further.
import * as React from "react";
import styles from "./AutoResizeTextarea.module.css";

function getLineHeightPx(el) {
  const computed = window.getComputedStyle(el);
  const lineHeight = parseFloat(computed.lineHeight);
  if (!Number.isNaN(lineHeight)) return lineHeight;
  // "normal" or unparsable line-height: fall back to a sane multiple of font size
  const fontSize = parseFloat(computed.fontSize) || 16;
  return fontSize * 1.2;
}

function getVerticalExtraPx(el) {
  const computed = window.getComputedStyle(el);
  const paddingTop = parseFloat(computed.paddingTop) || 0;
  const paddingBottom = parseFloat(computed.paddingBottom) || 0;
  const borderTop = parseFloat(computed.borderTopWidth) || 0;
  const borderBottom = parseFloat(computed.borderBottomWidth) || 0;
  // Our CSS module forces box-sizing: border-box, but guard anyway in case
  // something upstream changes it.
  return computed.boxSizing === "border-box"
    ? paddingTop + paddingBottom + borderTop + borderBottom
    : 0;
}

function AutoResizeTextarea_(
  {
    value,
    defaultValue = "",
    placeholder,
    minRows = 1,
    maxRows,
    autoFocus,
    disabled,
    readOnly,
    name,
    id,
    className,
    maxLength,
    submitOnEnter = false,
    dataTestId,
    placeholderColor,
    placeholderOpacity,
    onChange,
    onFocus,
    onBlur,
    onKeyDown,
    onInput,
    onEnterPress,
    ...rest
  },
  outerRef
) {
  const innerRef = React.useRef(null);
  const [uncontrolledValue, setUncontrolledValue] = React.useState(defaultValue);
  const isControlled = value !== undefined;
  const currentValue = isControlled ? value ?? "" : uncontrolledValue;

  const resize = React.useCallback(() => {
    const el = innerRef.current;
    if (!el) return;

    const lineHeight = getLineHeightPx(el);
    const extra = getVerticalExtraPx(el);
    const minHeight = lineHeight * Math.max(1, minRows) + extra;
    const maxHeight = maxRows ? lineHeight * maxRows + extra : Infinity;

    // Standard auto-height recipe: collapse, measure, then grow to fit.
    el.style.height = "auto";
    const scrollHeight = el.scrollHeight;
    const nextHeight = Math.min(Math.max(scrollHeight, minHeight), maxHeight);
    el.style.height = `${nextHeight}px`;
    // Only scroll internally once maxRows has actually been exceeded.
    el.style.overflowY = scrollHeight > maxHeight ? "auto" : "hidden";
  }, [minRows, maxRows]);

  // Covers all three required triggers: mount, every input/change (via
  // currentValue changing), and externally-controlled value changes.
  React.useLayoutEffect(() => {
    resize();
  }, [currentValue, resize]);

  // Recalculate when the field's own width changes (container resize,
  // responsive layout, Studio canvas resizing, font loading, etc.) since
  // that changes how many lines the content wraps into.
  React.useEffect(() => {
    const el = innerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(() => resize());
    observer.observe(el);
    return () => observer.disconnect();
  }, [resize]);

  React.useImperativeHandle(outerRef, () => ({
    focus: (opts) => innerRef.current?.focus(opts),
    blur: () => innerRef.current?.blur(),
    select: () => innerRef.current?.select(),
    getValue: () => innerRef.current?.value ?? "",
  }));

  const handleChange = (e) => {
    if (!isControlled) {
      setUncontrolledValue(e.target.value);
    }
    onChange?.(e.target.value, e);
  };

  const handleKeyDown = (e) => {
    if (submitOnEnter && e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onEnterPress?.(currentValue, e);
    }
    onKeyDown?.(e);
  };

  const cssVars = {};
  if (placeholderColor) cssVars["--arta-placeholder-color"] = placeholderColor;
  if (placeholderOpacity !== undefined && placeholderOpacity !== null) {
    cssVars["--arta-placeholder-opacity"] = placeholderOpacity;
  }

  return (
    <textarea
      {...rest}
      ref={innerRef}
      className={[styles.autoResizeTextarea, className].filter(Boolean).join(" ")}
      style={cssVars}
      value={currentValue}
      placeholder={placeholder}
      rows={minRows}
      name={name}
      id={id}
      maxLength={maxLength}
      autoFocus={autoFocus}
      disabled={disabled}
      readOnly={readOnly}
      data-testid={dataTestId}
      onChange={handleChange}
      onFocus={onFocus}
      onBlur={onBlur}
      onKeyDown={handleKeyDown}
      onInput={onInput}
    />
  );
}

const AutoResizeTextarea = React.forwardRef(AutoResizeTextarea_);
AutoResizeTextarea.displayName = "AutoResizeTextarea";

export default AutoResizeTextarea;
